/**
 * Earthquake Sound - Theme JavaScript
 */

(function() {
  'use strict';

  // Global theme object
  window.EarthquakeSound = window.EarthquakeSound || {};

  /**
   * Utility functions
   */
  const utils = {
    // Debounce function
    debounce(fn, delay) {
      let timeoutId;
      return function(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    // Format money
    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || window.theme.moneyFormat;

      function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) return 0;

        const parts = (number / 100).toFixed(precision).split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const cents = parts[1] ? decimal + parts[1] : '';

        return dollars + cents;
      }

      let value = '';
      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    },

    // Serialize form data
    serializeForm(form) {
      const formData = new FormData(form);
      const data = {};
      for (const [key, value] of formData.entries()) {
        data[key] = value;
      }
      return data;
    },

    // Fetch wrapper with error handling
    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    }
  };

  /**
   * Cart functionality
   */
  const cart = {
    async get() {
      return utils.fetchJSON(window.theme.routes.cart_url + '.js');
    },

    async add(id, quantity = 1) {
      const formData = new FormData();
      formData.append('id', id);
      formData.append('quantity', quantity);

      const response = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async update(updates) {
      const response = await utils.fetchJSON(window.theme.routes.cart_update_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ updates })
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
      return response;
    },

    async change(line, quantity) {
      const response = await utils.fetchJSON(window.theme.routes.cart_change_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity })
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
      return response;
    },

    updateCount(count) {
      const cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach(el => {
        el.textContent = count;
        el.classList.toggle('hidden', count === 0);
      });
    }
  };

  /**
   * Product form handling
   */
  class ProductForm {
    constructor(form) {
      this.form = form;
      this.submitBtn = form.querySelector('[type="submit"]');
      this.bindEvents();
    }

    bindEvents() {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      e.preventDefault();

      if (this.submitBtn.disabled) return;

      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('loading');

      const originalText = this.submitBtn.textContent;
      this.submitBtn.textContent = 'Adding...';

      try {
        const formData = new FormData(this.form);
        const id = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;

        await cart.add(id, quantity);

        // Handle based on cart type
        if (window.theme?.cartType === 'drawer') {
          document.dispatchEvent(new CustomEvent('cart:open'));
        } else if (window.theme?.cartType === 'notification') {
          this.showNotification();
        } else {
          // Redirect to cart page for page cart type
          window.location.href = window.theme.routes.cart_url;
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError('Could not add item to cart. Please try again.');
      } finally {
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('loading');
        this.submitBtn.textContent = originalText;
      }
    }

    showNotification() {
      // Simple notification
      const notification = document.createElement('div');
      notification.className = 'cart-notification animate-slide-down';
      notification.innerHTML = `
        <div class="cart-notification__content">
          <span>Added to cart!</span>
          <a href="${window.theme.routes.cart_url}" class="btn btn--sm btn--primary">View Cart</a>
        </div>
      `;
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    }

    showError(message) {
      const notification = document.createElement('div');
      notification.className = 'cart-notification cart-notification--error animate-slide-down';
      notification.innerHTML = `
        <div class="cart-notification__content">
          <span>${message}</span>
        </div>
      `;
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 4000);
    }
  }

  /**
   * Cart drawer
   */
  class CartDrawer {
    constructor() {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) return;

      this.overlay = document.querySelector('.overlay');
      this.closeBtn = this.drawer.querySelector('[data-cart-close]');
      this.content = this.drawer.querySelector('[data-cart-content]');

      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:updated', () => this.refresh());

      // Cart toggle is handled in header.liquid which dispatches 'cart:open'

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    open() {
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.refresh();
    }

    close() {
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    async refresh() {
      if (!this.content) return;

      try {
        const timestamp = Date.now();
        const response = await fetch(`${window.theme.routes.cart_url}?section_id=cart-drawer&t=${timestamp}`);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('[data-cart-content]');
        const newFooter = doc.querySelector('[data-cart-footer]');

        if (newContent) {
          this.content.innerHTML = newContent.innerHTML;
        }

        // Also update footer (checkout button visibility depends on cart state)
        const currentFooter = this.drawer.querySelector('[data-cart-footer]');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }

        // Update cart count
        const cartData = await cart.get();
        cart.updateCount(cartData.item_count);

        // Rebind events for new content
        this.bindContentEvents();
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }

    bindContentEvents() {
      // Close buttons in content area
      const contentCloseBtns = this.content.querySelectorAll('[data-cart-close]');
      contentCloseBtns.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close buttons in footer area (Continue Shopping)
      const footer = this.drawer.querySelector('[data-cart-footer]');
      if (footer) {
        const footerCloseBtns = footer.querySelectorAll('[data-cart-close]');
        footerCloseBtns.forEach(btn => {
          btn.addEventListener('click', () => this.close());
        });

        // Checkout button - ensure it navigates to checkout
        const checkoutBtn = footer.querySelector('[data-checkout-btn]');
        if (checkoutBtn) {
          checkoutBtn.addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = '/checkout';
          });
        }
      }

      // Note toggle
      const noteToggle = this.content.querySelector('[data-note-toggle]');
      const noteContent = this.content.querySelector('[data-note-content]');
      if (noteToggle && noteContent) {
        noteToggle.addEventListener('click', () => {
          const isExpanded = noteToggle.getAttribute('aria-expanded') === 'true';
          noteToggle.setAttribute('aria-expanded', !isExpanded);
          noteContent.classList.toggle('is-open', !isExpanded);
        });
      }

      // Quantity buttons
      const quantityBtns = this.content.querySelectorAll('[data-quantity-change]');
      quantityBtns.forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const line = parseInt(btn.dataset.line);
          const change = parseInt(btn.dataset.quantityChange);
          const input = btn.closest('.quantity-selector').querySelector('input');
          const newQuantity = Math.max(0, parseInt(input.value) + change);

          await cart.change(line, newQuantity);
        });
      });

      // Remove buttons
      const removeBtns = this.content.querySelectorAll('[data-remove-item]');
      removeBtns.forEach(btn => {
        btn.addEventListener('click', async () => {
          const line = parseInt(btn.dataset.line);
          await cart.change(line, 0);
        });
      });
    }
  }

  /**
   * Mobile menu
   */
  class MobileMenu {
    constructor() {
      this.menu = document.getElementById('mobile-menu');
      this.toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      this.overlay = document.querySelector('.overlay');

      if (!this.menu || !this.toggleBtn) return;

      this.bindEvents();
    }

    bindEvents() {
      this.toggleBtn.addEventListener('click', () => this.toggle());

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.menu.classList.contains('is-open');
    }

    toggle() {
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      this.menu.classList.add('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      if (this.overlay) this.overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.menu.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      if (this.overlay) this.overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  }

  /**
   * Header scroll behavior
   */
  class StickyHeader {
    constructor() {
      this.header = document.getElementById('site-header');
      if (!this.header) return;

      this.lastScrollY = 0;
      this.bindEvents();
    }

    bindEvents() {
      window.addEventListener('scroll', utils.debounce(() => this.onScroll(), 10));
    }

    onScroll() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 100) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      if (currentScrollY > this.lastScrollY && currentScrollY > 200) {
        this.header.classList.add('is-hidden');
      } else {
        this.header.classList.remove('is-hidden');
      }

      this.lastScrollY = currentScrollY;
    }
  }

  /**
   * Variant selector
   */
  class VariantSelector {
    constructor(container) {
      this.container = container;
      this.productData = JSON.parse(container.querySelector('[data-product-json]')?.textContent || '{}');
      this.options = container.querySelectorAll('[data-option-selector]');
      this.variantIdInput = container.querySelector('[name="id"]');
      this.priceEl = container.querySelector('[data-product-price]');
      this.comparePriceEl = container.querySelector('[data-compare-price]');
      this.addToCartBtn = container.querySelector('[data-add-to-cart]');

      if (!this.options.length || !this.productData.variants) return;

      this.bindEvents();
    }

    bindEvents() {
      this.options.forEach(option => {
        option.addEventListener('change', () => this.onOptionChange());
      });
    }

    onOptionChange() {
      const selectedOptions = [];
      this.options.forEach(option => {
        selectedOptions.push(option.value);
      });

      const variant = this.productData.variants.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (variant) {
        this.updateVariant(variant);
      }
    }

    updateVariant(variant) {
      // Update variant ID
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update price
      if (this.priceEl) {
        this.priceEl.textContent = utils.formatMoney(variant.price);
      }

      if (this.comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceEl.textContent = utils.formatMoney(variant.compare_at_price);
          this.comparePriceEl.classList.remove('hidden');
        } else {
          this.comparePriceEl.classList.add('hidden');
        }
      }

      // Update button state
      if (this.addToCartBtn) {
        if (variant.available) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = this.addToCartBtn.dataset.addToCart || 'Add to Cart';
        } else {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.textContent = this.addToCartBtn.dataset.soldOut || 'Sold Out';
        }
      }

      // Dispatch event for other components
      this.container.dispatchEvent(new CustomEvent('variant:changed', { detail: variant }));
    }
  }

  /**
   * Quantity selector
   */
  class QuantitySelector {
    constructor(container) {
      this.container = container;
      this.input = container.querySelector('input');
      this.decrementBtn = container.querySelector('[data-quantity-decrement]');
      this.incrementBtn = container.querySelector('[data-quantity-increment]');

      if (!this.input) return;

      this.min = parseInt(this.input.min) || 1;
      this.max = parseInt(this.input.max) || 999;

      this.bindEvents();
    }

    bindEvents() {
      this.decrementBtn?.addEventListener('click', () => this.decrement());
      this.incrementBtn?.addEventListener('click', () => this.increment());
      this.input.addEventListener('change', () => this.validate());
    }

    decrement() {
      const value = parseInt(this.input.value) - 1;
      this.input.value = Math.max(this.min, value);
      this.input.dispatchEvent(new Event('change'));
    }

    increment() {
      const value = parseInt(this.input.value) + 1;
      this.input.value = Math.min(this.max, value);
      this.input.dispatchEvent(new Event('change'));
    }

    validate() {
      let value = parseInt(this.input.value);
      if (isNaN(value) || value < this.min) value = this.min;
      if (value > this.max) value = this.max;
      this.input.value = value;
    }
  }

  /**
   * Accordion
   */
  class Accordion {
    constructor(container) {
      this.container = container;
      this.items = container.querySelectorAll('[data-accordion-item]');

      this.bindEvents();
    }

    bindEvents() {
      this.items.forEach(item => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        trigger?.addEventListener('click', () => this.toggle(item));
      });
    }

    toggle(item) {
      const isOpen = item.classList.contains('is-open');
      const content = item.querySelector('[data-accordion-content]');

      if (isOpen) {
        item.classList.remove('is-open');
        content.style.maxHeight = null;
      } else {
        item.classList.add('is-open');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    }
  }

  /**
   * Tabs
   */
  class Tabs {
    constructor(container) {
      this.container = container;
      this.tabs = container.querySelectorAll('[data-tab]');
      this.panels = container.querySelectorAll('[data-tab-panel]');

      this.bindEvents();
    }

    bindEvents() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.activate(tab.dataset.tab));
      });
    }

    activate(tabId) {
      this.tabs.forEach(tab => {
        tab.classList.toggle('is-active', tab.dataset.tab === tabId);
        tab.setAttribute('aria-selected', tab.dataset.tab === tabId);
      });

      this.panels.forEach(panel => {
        panel.classList.toggle('is-active', panel.dataset.tabPanel === tabId);
        panel.hidden = panel.dataset.tabPanel !== tabId;
      });
    }
  }

  /**
   * Image gallery
   */
  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainImage = container.querySelector('[data-main-image]');
      this.thumbnails = container.querySelectorAll('[data-thumbnail]');

      if (!this.mainImage || !this.thumbnails.length) return;

      this.bindEvents();
    }

    bindEvents() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => this.setMainImage(thumb));
      });

      // Listen for variant changes
      document.addEventListener('variant:changed', (e) => {
        const variant = e.detail;
        if (variant.featured_image) {
          this.setMainImageSrc(variant.featured_image.src);
        }
      });
    }

    setMainImage(thumb) {
      const src = thumb.dataset.fullSrc || thumb.src;
      this.setMainImageSrc(src);

      this.thumbnails.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }

    setMainImageSrc(src) {
      this.mainImage.src = src;
    }
  }

  /**
   * Product card actions (add to cart, quick view)
   * Uses event delegation for dynamically loaded content
   */
  class ProductCardActions {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      // Event delegation for add to cart forms in product cards
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('.product-card__action-form');
        if (!form) return;

        e.preventDefault();

        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn.disabled) return;

        submitBtn.disabled = true;
        submitBtn.classList.add('loading');

        try {
          const formData = new FormData(form);
          const id = formData.get('id');
          const quantity = parseInt(formData.get('quantity')) || 1;

          await cart.add(id, quantity);

          // Handle cart type
          if (window.theme?.cartType === 'drawer') {
            document.dispatchEvent(new CustomEvent('cart:open'));
          } else if (window.theme?.cartType === 'notification') {
            this.showNotification();
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
        } finally {
          submitBtn.disabled = false;
          submitBtn.classList.remove('loading');
        }
      });

      // Event delegation for quick view buttons
      document.addEventListener('click', (e) => {
        const quickViewBtn = e.target.closest('[data-quick-view]');
        if (!quickViewBtn) return;

        e.preventDefault();
        const productUrl = quickViewBtn.dataset.quickView;

        if (productUrl) {
          this.openQuickView(productUrl);
        }
      });
    }

    showNotification() {
      const notification = document.createElement('div');
      notification.className = 'cart-notification animate-slide-down';
      notification.innerHTML = `
        <div class="cart-notification__content">
          <span>Added to cart!</span>
          <a href="${window.theme?.routes?.cart_url || '/cart'}" class="btn btn--sm btn--primary">View Cart</a>
        </div>
      `;
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    }

    async openQuickView(productUrl) {
      // Check if quick view modal exists, otherwise redirect to product page
      let modal = document.getElementById('quick-view-modal');

      if (!modal) {
        // Create modal if it doesn't exist
        modal = document.createElement('div');
        modal.id = 'quick-view-modal';
        modal.className = 'quick-view-modal';
        modal.innerHTML = `
          <div class="quick-view-modal__overlay" data-quick-view-close></div>
          <div class="quick-view-modal__content">
            <button type="button" class="quick-view-modal__close" data-quick-view-close aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div class="quick-view-modal__body" data-quick-view-body>
              <div class="quick-view-modal__loading">Loading...</div>
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        // Bind close events
        modal.querySelectorAll('[data-quick-view-close]').forEach(el => {
          el.addEventListener('click', () => this.closeQuickView());
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && modal.classList.contains('is-open')) {
            this.closeQuickView();
          }
        });
      }

      const body = modal.querySelector('[data-quick-view-body]');
      body.innerHTML = '<div class="quick-view-modal__loading">Loading...</div>';

      // Open modal
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      try {
        // Fetch product section
        const response = await fetch(productUrl + '?section_id=product-quick-view');
        let html = await response.text();

        // If quick view section doesn't exist, try main product section
        if (!html.trim() || html.includes('Liquid error')) {
          const mainResponse = await fetch(productUrl + '?section_id=main-product');
          html = await mainResponse.text();
        }

        body.innerHTML = html || `
          <div class="quick-view-modal__fallback">
            <p>Unable to load product preview.</p>
            <a href="${productUrl}" class="btn btn--primary">View Product</a>
          </div>
        `;

        // Initialize any product forms in the quick view
        const forms = body.querySelectorAll('[data-product-form]');
        forms.forEach(form => new ProductForm(form));

        // Initialize quantity selectors
        body.querySelectorAll('.quantity-selector').forEach(container => {
          new QuantitySelector(container);
        });

      } catch (error) {
        console.error('Error loading quick view:', error);
        body.innerHTML = `
          <div class="quick-view-modal__fallback">
            <p>Unable to load product preview.</p>
            <a href="${productUrl}" class="btn btn--primary">View Product</a>
          </div>
        `;
      }
    }

    closeQuickView() {
      const modal = document.getElementById('quick-view-modal');
      if (modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }
  }

  /**
   * Collection filters
   */
  class CollectionFilters {
    constructor(container) {
      this.container = container;
      this.form = container.querySelector('[data-filter-form]');
      this.productsContainer = document.querySelector('[data-products-container]');

      if (!this.form) return;

      this.bindEvents();
    }

    bindEvents() {
      this.form.addEventListener('change', utils.debounce(() => this.apply(), 300));

      // Price range inputs
      const priceInputs = this.form.querySelectorAll('[data-price-filter]');
      priceInputs.forEach(input => {
        input.addEventListener('input', utils.debounce(() => this.apply(), 500));
      });
    }

    async apply() {
      const formData = new FormData(this.form);
      const params = new URLSearchParams(formData);

      // Update URL
      const url = new URL(window.location);
      url.search = params.toString();
      window.history.pushState({}, '', url);

      // Fetch filtered results
      try {
        this.productsContainer.classList.add('loading');
        const response = await fetch(url + '&section_id=main-collection-products');
        const html = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProducts = doc.querySelector('[data-products-container]');

        if (newProducts) {
          this.productsContainer.innerHTML = newProducts.innerHTML;
        }
      } catch (error) {
        console.error('Error applying filters:', error);
      } finally {
        this.productsContainer.classList.remove('loading');
      }
    }
  }

  /**
   * Search
   */
  class PredictiveSearch {
    constructor() {
      this.input = document.querySelector('[data-search-input]');
      this.results = document.querySelector('[data-search-results]');

      if (!this.input) return;

      this.bindEvents();
    }

    bindEvents() {
      this.input.addEventListener('input', utils.debounce(() => this.search(), 300));
      this.input.addEventListener('focus', () => this.onFocus());

      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-search]')) {
          this.close();
        }
      });
    }

    async search() {
      const query = this.input.value.trim();

      if (query.length < 3) {
        this.close();
        return;
      }

      try {
        const response = await fetch(
          `${window.theme.routes.search_url}/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
        );
        const data = await response.json();

        this.render(data.resources.results.products);
      } catch (error) {
        console.error('Search error:', error);
      }
    }

    render(products) {
      if (!this.results) return;

      if (!products || products.length === 0) {
        this.results.innerHTML = '<p class="search-results__empty">No products found</p>';
        this.open();
        return;
      }

      const html = products.map(product => `
        <a href="${product.url}" class="search-result">
          ${product.featured_image ? `<img src="${product.featured_image.url}" alt="${product.title}" class="search-result__image">` : ''}
          <div class="search-result__content">
            <p class="search-result__title">${product.title}</p>
            <p class="search-result__price">${utils.formatMoney(product.price)}</p>
          </div>
        </a>
      `).join('');

      this.results.innerHTML = html;
      this.open();
    }

    onFocus() {
      if (this.input.value.length >= 3) {
        this.search();
      }
    }

    open() {
      this.results?.classList.add('is-open');
    }

    close() {
      this.results?.classList.remove('is-open');
    }
  }

  /**
   * Initialize all components
   */
  function init() {
    // Global components
    new CartDrawer();
    new MobileMenu();
    new StickyHeader();
    new PredictiveSearch();
    new ProductCardActions();

    // Product forms
    document.querySelectorAll('[data-product-form]').forEach(form => {
      new ProductForm(form);
    });

    // Variant selectors
    document.querySelectorAll('[data-variant-selector]').forEach(container => {
      new VariantSelector(container);
    });

    // Quantity selectors
    document.querySelectorAll('.quantity-selector').forEach(container => {
      new QuantitySelector(container);
    });

    // Accordions
    document.querySelectorAll('[data-accordion]').forEach(container => {
      new Accordion(container);
    });

    // Tabs
    document.querySelectorAll('[data-tabs]').forEach(container => {
      new Tabs(container);
    });

    // Product galleries
    document.querySelectorAll('[data-product-gallery]').forEach(container => {
      new ProductGallery(container);
    });

    // Collection filters
    document.querySelectorAll('[data-collection-filters]').forEach(container => {
      new CollectionFilters(container);
    });
  }

  // Export utilities and cart for external use
  window.EarthquakeSound.utils = utils;
  window.EarthquakeSound.cart = cart;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
